/**
 * Australian state / territory ranging.
 *
 * Products carry `available_states`. An EMPTY array means "not yet ranged /
 * assumed national" and stays visible to everyone — that's the safe default so
 * a missing probe can never hide stock from a shopper.
 */

export const AU_STATES = [
  { code: "NSW", name: "New South Wales" },
  { code: "VIC", name: "Victoria" },
  { code: "QLD", name: "Queensland" },
  { code: "WA", name: "Western Australia" },
  { code: "SA", name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NT", name: "Northern Territory" },
] as const;

export type StateCode = (typeof AU_STATES)[number]["code"];

const CODES = new Set(AU_STATES.map((s) => s.code as string));

export function isStateCode(v: unknown): v is StateCode {
  return typeof v === "string" && CODES.has(v);
}

export function stateName(code: string | null | undefined): string {
  return AU_STATES.find((s) => s.code === code)?.name ?? code ?? "";
}

/** localStorage key holding the state picked at sign-up / by a guest. */
export const STATE_STORAGE_KEY = "valu.state";

export function readLocalState(): StateCode | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STATE_STORAGE_KEY);
    return isStateCode(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeLocalState(code: StateCode | null) {
  if (typeof window === "undefined") return;
  try {
    if (code) localStorage.setItem(STATE_STORAGE_KEY, code);
    else localStorage.removeItem(STATE_STORAGE_KEY);
  } catch {
    /* ignore storage failures */
  }
}

/**
 * PostgREST `.or()` expression that keeps un-ranged products (empty array) and
 * products explicitly ranged in the given state.
 */
export function stateOrFilter(code: StateCode): string {
  return `available_states.eq.{},available_states.cs.{${code}}`;
}

/** True when a product is explicitly ranged and the shopper's state isn't in it. */
export function isOutOfState(
  availableStates: string[] | null | undefined,
  code: string | null | undefined,
): boolean {
  if (!code || !availableStates || availableStates.length === 0) return false;
  return !availableStates.includes(code);
}

/**
 * Short label for a state-ranged product, e.g. "WA" or "SA, VIC".
 * Returns null when the product is national (empty = un-ranged, or all 8
 * states), so national lines never get a badge.
 */
export function stateBadgeLabel(
  availableStates: string[] | null | undefined,
): string | null {
  if (!availableStates || availableStates.length === 0) return null;
  const codes = availableStates.filter(isStateCode);
  if (codes.length === 0 || codes.length >= AU_STATES.length) return null;
  return [...codes]
    .sort((a, b) => AU_STATES.findIndex((s) => s.code === a) - AU_STATES.findIndex((s) => s.code === b))
    .join(", ");
}

